import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { UtilsExternal } from 'utils/external';
import {
  EExternalUpdaterEvent,
  fireEvent
} from 'utils/generic/event-bus/external';
import { GLOBALS } from 'utils/generic/global-variables';
import RidGenerator from 'utils/swarm/rid-generator';
import { NotificationsTypes } from 'interfaces/authentication';
import { BetTicketMin } from 'interfaces/bet-data';
import { UserTriggeringRules } from 'interfaces/notifications';
import {
  authCommand,
  subscribeToUserNotifications,
  unsubscribeCommand
} from 'services/authentication';
import {
  setCashbackNotifications,
  setLoyaltyNotifications
} from 'store/actions';
import { updateBetTickets } from 'store/actions/betslip';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

export const markAsReadNotification = (
  id: number | number[],
  list?: boolean
) => {
  authCommand(
    {
      command: CommandNames.MARK_AS_READ_NOTIFICATION,
      params: { [list ? 'notification_id_list' : 'notification_id']: id },
      rid: RidGenerator.gForCommand()
    },
    null
  );
};

export const useUserNotifications = (): void => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const user = useSelector(getUser);
  const dispatch = useDispatch();

  const openPopupTriggering = (data: any) => {
    if (window.refreshWhenLoggedIn && window.mustRefreshForSessionVisibility) {
      return false;
    }

    const popupTriggeringRules: UserTriggeringRules[] = [];
    let popupOpened = false;

    const comparision: Record<string, string> = {
      '1': 'is',
      '2': 'isNot',
      '3': 'startsWith',
      '4': 'endsWith',
      '5': 'contains',
      '6': 'doesNotContains'
    };

    for (const key in data) {
      const rule: UserTriggeringRules = {};

      if (
        data[key].ClientId === user.id &&
        data[key].Id &&
        !data[key].Page?.Value
      ) {
        let openedPopupsList = LocalStorage.getItem(
          'openedPopupsListByLanguage'
        );

        const currentLanguageId = GLOBALS.languageConfigs.id;
        openedPopupsList = openedPopupsList ? JSON.parse(openedPopupsList) : {};

        if (
          openedPopupsList &&
          currentLanguageId &&
          openedPopupsList[currentLanguageId] &&
          openedPopupsList[currentLanguageId].length &&
          openedPopupsList[currentLanguageId].indexOf(data[key].Id) + 1
        ) {
          popupOpened = false;
        } else {
          if (window.fromNext) {
            UtilsExternal.openNativePopup(data[key].Alias);
          } else {
            window.openPopup?.(data[key].Alias);
          }

          markAsReadNotification(+key);
          popupOpened = true;
          break;
        }
      }

      if (
        data[key].ClientId === user.id &&
        data[key].Id &&
        data[key].Page.Comparison &&
        data[key].Page.Value
      ) {
        rule.triggeringUrlValue = data[key].Page.Value;
        rule.triggeringUrlRule =
          comparision[data[key].Page.Comparison as keyof typeof comparision];
        rule.alias = data[key].Alias;
        rule.id = data[key].Id;
        rule.enableTriggeringRule = 1;
        rule.triggeringTime = 0;
        rule.key = +key;
        popupTriggeringRules.push(rule);
      }
    }

    if (window.fromNext) {
      if (!popupOpened && popupTriggeringRules.length) {
        fireEvent({
          key: EExternalUpdaterEvent.ADD_TRIGGERING_RULES,
          payload: popupTriggeringRules.reduce((acc, rule) => {
            if (rule.alias) {
              acc[rule.alias] = rule;
            }

            return acc;
          }, {} as Record<string, UserTriggeringRules>)
        });
      }

      return;
    }

    if (
      !popupOpened &&
      popupTriggeringRules.length &&
      !window.triggeringRuleIsWorked
    ) {
      window.popupTriggeringRules = popupTriggeringRules;
      window.openPopupsWithInterval?.(null, true);
    }
  };

  const setCashbackData = (data: {
    [key: string]: { cashback: NotificationsTypes };
  }) => {
    const tickets = Object.entries(data || {});

    if (tickets.length) {
      const cashbackData: Array<NotificationsTypes> = [];

      tickets.forEach(([ticketId, ticket]) => {
        if (ticket.cashback) {
          cashbackData.push({
            ...ticket.cashback,
            id: +ticketId,
            Amount: ticket.cashback.Amount || 0,
            RewordType: ticket.cashback.RewordType || 0,
            markAsRead: false,
            bonusType: 'cashback'
          });
        }
      });

      dispatch(setCashbackNotifications(cashbackData));
    }
  };

  const setLoyaltyData = (data: {
    [key: string]: { LevelUpBonus: NotificationsTypes };
  }) => {
    const tickets = Object.entries(data || {});

    if (tickets.length) {
      const loyaltyData: Array<NotificationsTypes> = [];

      tickets.forEach(([ticketId, ticket]) => {
        if (ticket.LevelUpBonus) {
          loyaltyData.push({
            ...ticket.LevelUpBonus,
            id: +ticketId,
            Amount: ticket.LevelUpBonus.Amount || 0,
            bonusType: 'loyalty'
          });
        }
      });

      dispatch(setLoyaltyNotifications(loyaltyData));
    }
  };

  useEffect(() => {
    const rid = RidGenerator.gForSubscribe();

    if (
      isLoggedIn &&
      !user.pending &&
      !window.mustRefreshForSessionVisibility
    ) {
      subscribeToUserNotifications(
        (data: any) => {
          openPopupTriggering(data);
          setCashbackData(data);
          setLoyaltyData(data);
        },
        (data: any) => {
          if (data && data.cashout) {
            const tickets = Object.keys(data.cashout).map(
              (ticketId: string) =>
                ({
                  id: +ticketId,
                  amount: data.cashout[ticketId].amount
                } as BetTicketMin)
            );

            dispatch(updateBetTickets(tickets));
          }

          openPopupTriggering(data);
          setCashbackData(data);
          setLoyaltyData(data);
        },
        rid
      );
    }

    return () => {
      unsubscribeCommand(rid);
    };
  }, [isLoggedIn, user.pending]);
};
